import axios from "axios";
import ErrorAlert from "../components/Alerts/ErrorAlert";
import {
  clearClientSession,
  clearInvalidBranchContext,
  clearInvalidBusinessContext,
  emitAuthInvalid,
  emitContextInvalid,
} from "../utils/ownershipContext";

let configured = false;
let lastAlertAt = 0;

// Misma key usada en src/i18n/index.js (LANG_STORAGE_KEY). No se importa directo de ahi
// para mantener este archivo sin dependencias (se carga antes que todo lo demas en index.js).
const LANG_STORAGE_KEY = "lang";

const getCurrentLanguage = () => {
  try {
    return window.localStorage.getItem(LANG_STORAGE_KEY) || undefined;
  } catch (err) {
    return undefined;
  }
};

const installRequestInterceptor = (instance) => {
  if (!instance || instance.__smartpayLangInterceptor) return instance;

  instance.interceptors.request.use((config) => {
    if (config.skipLanguageParam) {
      return config;
    }

    const lang = getCurrentLanguage();

    // Solo agregamos ?lang= si el caller no lo definio ya explicitamente
    if (lang && (!config.params || config.params.lang === undefined)) {
      config.params = { ...(config.params || {}), lang };
    }

    return config;
  });

  instance.__smartpayLangInterceptor = true;
  return instance;
};

const getUrl = (error) => {
  const baseURL = error?.config?.baseURL || "";
  const url = error?.config?.url || "";
  return `${baseURL}${url}`;
};

const showThrottledAlert = (title, icon = "error") => {
  const now = Date.now();
  if (now - lastAlertAt < 1200) return;
  lastAlertAt = now;
  ErrorAlert("", title, icon, 6000);
};

const isClientResource = (url) => {
  return /\/client\/me\b|\/direction\/client\b|\/frequent-shopper\/client\b|\/client\/[^/]+/i.test(url);
};

const isBusinessResource = (url) => {
  return /\/business\/(?:config\/)?[^/]+$/i.test(url);
};

const isBranchOrMachineResource = (url) => {
  return /branchoffice|branch_machine|category_per_machine|\/machines\/|\/machine\/|auth\/business\/smartpay|delivery-fees-rules|pick-and-go/i.test(url);
};

const isPublicEcommerceResource = (url) => {
  return /\/auth\/token(?:IsValid)?\b|\/auth\/business\/smartpay\b|\/ecommerce\/[^/]+\/content\b|delivery-fees-rules|pick-and-go/i.test(url);
};

const installResponseInterceptor = (instance) => {
  if (!instance || instance.__smartpayOwnershipInterceptor) return instance;

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;

      if (!status || error?.config?.skipOwnershipErrorHandler) {
        return Promise.reject(error);
      }

      const url = getUrl(error);

      if (status === 401) {
        if (isPublicEcommerceResource(url)) {
          return Promise.reject(error);
        }

        clearClientSession();
        emitAuthInvalid({ status, url });
        showThrottledAlert("Tu sesion expiro. Inicia sesion nuevamente.", "warning");
        return Promise.reject(error);
      }

      if (status === 403) {
        showThrottledAlert("No tienes permiso para realizar esta operacion.", "warning");
        return Promise.reject(error);
      }

      if (status === 404) {
        if (isPublicEcommerceResource(url)) {
          // delivery-fees-rules / pick-and-go can legitimately 404 just because a business has
          // none configured yet, or because business.id wasn't resolved yet on first load - that's
          // not evidence the branch/machine context is broken, so don't nuke it and bounce the user
          // around for it (same reasoning already applied to these on the 401 branch above).
          return Promise.reject(error);
        }

        if (isClientResource(url)) {
          clearClientSession();
          emitAuthInvalid({ status, url });
          showThrottledAlert("La sesion del cliente ya no esta disponible.", "warning");
        } else if (isBusinessResource(url)) {
          clearInvalidBusinessContext();
          emitContextInvalid({ status, url, scope: "business" });
          showThrottledAlert("El comercio no esta disponible en este contexto.", "warning");
        } else if (isBranchOrMachineResource(url)) {
          clearInvalidBranchContext();
          emitContextInvalid({ status, url, scope: "branch" });
          showThrottledAlert("La sucursal o caja seleccionada ya no esta disponible.", "warning");
        }
      }

      return Promise.reject(error);
    }
  );

  instance.__smartpayOwnershipInterceptor = true;
  return instance;
};

export function configureAxiosOwnershipInterceptors() {
  if (configured) return;
  configured = true;

  installResponseInterceptor(axios);
  installRequestInterceptor(axios);

  const originalCreate = axios.create.bind(axios);
  axios.create = (...args) => installRequestInterceptor(installResponseInterceptor(originalCreate(...args)));
}

configureAxiosOwnershipInterceptors();
